import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { BarChart3, Layers, QrCode } from 'lucide-react';
export const Route = createFileRoute('/_layout/what-is-adora/')({
    component: WhatIsAdoraPage,
    head: () => ({
        meta: [
            { title: 'What Is Adora? — Offline Campaign Analytics Platform' },
            {
                name: 'description',
                content: 'Adora is an offline-first campaign analytics platform. Turn QR codes, flyers, menus, and signage into measurable campaigns — and understand what actually works.',
            },
            { property: 'og:title', content: 'What Is Adora? — Offline Campaign Analytics Platform' },
            {
                property: 'og:description',
                content: 'Adora is an offline-first campaign analytics platform. Turn QR codes, flyers, menus, and signage into measurable campaigns — and understand what actually works.',
            },
        ],
    }),
});
const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0 },
};
function WhatIsAdoraPage() {
    return (<motion.main className="mx-auto max-w-6xl px-6 md:px-8 py-12 md:py-16" initial="hidden" animate="visible" transition={{ staggerChildren: 0.08 }}>
            {/* HERO */}
            <motion.section className="max-w-3xl space-y-4" variants={fadeUp}>
                <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs text-muted-foreground">
                    <BarChart3 className="h-3.5 w-3.5"/>
                    Definition
                </span>

                <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">What is Adora?</h1>

                <p className="text-muted-foreground text-base md:text-lg">
                    <strong>Adora</strong> is an <strong>offline-first campaign analytics platform</strong> that helps
                    businesses measure how real-world materials — QR codes, short links, menus, packaging, flyers, and
                    signage — actually perform.
                </p>
            </motion.section>

            <Separator className="my-10"/>

            {/* THE PROBLEM */}
            <motion.section className="grid gap-6 md:grid-cols-2" variants={fadeUp}>
                <div className="space-y-3">
                    <h2 className="text-xl font-semibold">The problem Adora solves</h2>
                    <p className="text-sm text-muted-foreground">
                        Businesses spend money on printed materials every day — but rarely know which ones work.
                    </p>
                    <ul className="text-sm text-muted-foreground list-disc pl-4 space-y-1">
                        <li>Which menu placement drives scans?</li>
                        <li>Which flyer or poster actually converts?</li>
                        <li>Does location matter more than design?</li>
                    </ul>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Without Adora</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        <p>• Decisions based on intuition</p>
                        <p>• No visibility into offline performance</p>
                        <p>• Same QR used everywhere, no context</p>
                    </CardContent>
                </Card>
            </motion.section>

            <Separator className="my-10"/>

            {/* HOW ADORA WORKS */}
            <motion.section className="space-y-6" variants={fadeUp}>
                <h2 className="text-xl font-semibold">How Adora works</h2>

                <div className="grid gap-4 md:grid-cols-3">
                    <Feature icon={<Layers className="h-5 w-5"/>} title="Campaigns" body="Each promotion becomes a campaign. Menus, packaging, posters — all tracked separately."/>
                    <Feature icon={<QrCode className="h-5 w-5"/>} title="Smart entry points" body="QR codes and short links act as measurable entry points, not just redirects."/>
                    <Feature icon={<BarChart3 className="h-5 w-5"/>} title="Clear analytics" body="See scans, unique visitors, and performance by placement or idea."/>
                </div>
            </motion.section>

            <Separator className="my-10"/>

            {/* WHAT ADORA IS NOT */}
            <motion.section className="space-y-4" variants={fadeUp}>
                <h2 className="text-xl font-semibold">What Adora is not</h2>

                <div className="grid gap-4 sm:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Not a link-in-bio tool</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground">
                            Adora is not designed to group links for social profiles. It’s designed to measure discovery
                            and intent.
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Not just a QR generator</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground">
                            QR codes are just the surface. Adora focuses on understanding performance over time.
                        </CardContent>
                    </Card>
                </div>
            </motion.section>

            <Separator className="my-10"/>

            {/* WHO IT IS FOR */}
            <motion.section className="space-y-6" variants={fadeUp}>
                <h2 className="text-xl font-semibold">Who Adora is for</h2>

                <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                    <Audience label="Restaurants & cafés"/>
                    <Audience label="Retail & packaging brands"/>
                    <Audience label="Printers & agencies"/>
                    <Audience label="Events & street campaigns"/>
                    <Audience label="Offline-first businesses"/>
                </div>
            </motion.section>

            <Separator className="my-12"/>

            {/* NEXT STEPS */}
            <motion.section className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6" variants={fadeUp}>
                <div className="space-y-2">
                    <h3 className="text-lg font-semibold">Want to go deeper?</h3>
                    <p className="text-sm text-muted-foreground max-w-xl">
                        Learn how Adora tracks offline campaigns in practice, or compare it with other tools.
                    </p>
                </div>

                <div className="flex gap-2">
                    <Button asChild>
                        <Link to="/how-it-works">How Adora works</Link>
                    </Button>
                    <Button variant="outline" asChild>
                        <Link to="/compare">Compare tools</Link>
                    </Button>
                </div>
            </motion.section>
        </motion.main>);
}
/* ---------- helpers ---------- */
function Feature({ icon, title, body }) {
    return (<Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                        {icon}
                    </span>
                    {title}
                </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">{body}</CardContent>
        </Card>);
}
function Audience({ label }) {
    return <div className="rounded-lg border px-4 py-3 text-sm text-muted-foreground">{label}</div>;
}
